import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Progress } from "../../components/ui/progress";
import { Alert, AlertDescription, AlertTitle } from "../../components/ui/alert";
import { Skeleton } from "../../components/ui/skeleton";
import {
  ArrowLeft,
  Play,
  Square,
  RotateCcw,
  Clock,
  Cpu,
  HardDrive,
  AlertTriangle,
  CheckCircle2,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";
import {
  usePracticeProjectQuery,
  useEnrollPracticeProjectMutation,
} from "../../api/practiceProjectsApi";
import { useJupyterWorkspace } from "../../contexts/JupyterWorkspaceContext";
import { useAuth } from "../../App";
import paths from "../../routes";

/**
 * Practice Project Workspace Page
 * 
 * Provides a workspace environment for students to work on practice projects.
 * Integrates with JupyterHub to launch and manage Jupyter Lab workspaces.
 * 
 * Features:
 * - Launch JupyterHub workspace for practice project
 * - Display workspace iframe with Jupyter Lab
 * - Show workspace status and resource info
 * - Track workspace session and timing
 * - Auto-save and session management
 * 
 * Flow:
 * 1. Check if user is enrolled in project
 * 2. Initialize/restore workspace session
 * 3. Launch JupyterHub workspace (if needed)
 * 4. Display embedded Jupyter Lab
 * 5. Track activity and auto-save
 */

interface WorkspaceStatus {
  status: "none" | "provisioning" | "active" | "error" | "stopping";
  sessionId: string | null;
  workspaceUrl: string | null;
  serverReady: boolean;
  provisioningProgress: number;
  errorMessage?: string;
}

export default function PracticeProjectWorkspacePage() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { workspace, setWorkspace, updateWorkspace } = useJupyterWorkspace();

  // Redirect to notebook page - this workspace page is deprecated for practice projects
  useEffect(() => {
    if (projectId) {
      navigate(`/notebook/${projectId}`, {
        replace: true,
        state: {
          project: {
            id: projectId,
            isPracticeProject: true,
          },
        },
      });
    }
  }, [projectId, navigate]);

  const [workspaceStatus, setWorkspaceStatus] = useState<WorkspaceStatus>({
    status: "none",
    sessionId: null,
    workspaceUrl: null,
    serverReady: false,
    provisioningProgress: 0,
  });

  // Queries
  const { data: project, isLoading: projectLoading } = usePracticeProjectQuery(
    projectId!,
    { enabled: !!projectId }
  );

  // Mutations
  const enrollMutation = useEnrollPracticeProjectMutation();

  // Check enrollment status (simplified - check if user has enrolled)
  const isEnrolled = project?.data?.status === 'published'; // Simplified for now

  // Handle workspace launch - redirect to custom notebook
  const handleLaunchWorkspace = async () => {
    if (!projectId) return;

    try {
      // Ensure user is enrolled
      if (!isEnrolled) {
        await enrollMutation.mutateAsync(projectId);
        toast.success("Enrolled in project!");
      }

      // Navigate to custom notebook page
      navigate(`/notebook/${projectId}`, {
        state: {
          project: {
            id: projectId,
            title: project?.data?.title,
            isPracticeProject: true,
          },
        },
      });
    } catch (error: any) {
      console.error("Failed to enroll:", error);
      toast.error(error.message || "Failed to enroll in project");
    }
  };

  // Handle workspace stop
  const handleStopWorkspace = () => {
    setWorkspaceStatus({
      status: "none",
      sessionId: null,
      workspaceUrl: null,
      serverReady: false,
      provisioningProgress: 0,
    });
    updateWorkspace({ status: "none", serverReady: false });
    toast.info("Workspace stopped");
  };

  // Restore workspace from context on mount
  useEffect(() => {
    if (workspace.workspaceUrl && workspace.status === "active") {
      setWorkspaceStatus({
        status: "active",
        sessionId: workspace.sessionId,
        workspaceUrl: workspace.workspaceUrl,
        serverReady: workspace.serverReady,
        provisioningProgress: 100,
      });
    }
  }, []);

  if (projectLoading) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        <Skeleton className="h-8 w-64 mb-4" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  if (!project?.data) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Project Not Found</AlertTitle>
          <AlertDescription>
            The practice project you're looking for doesn't exist or has been
            removed.
          </AlertDescription>
        </Alert>
        <Button
          variant="outline"
          onClick={() => navigate(paths.practiceProjects)}
          className="mt-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Browse
        </Button>
      </div>
    );
  }

  const practiceProject = project.data;

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between max-w-full">
          {/* Left: Back button and title */}
          <div className="flex items-center gap-4 flex-1 min-w-0">
            <Button
              variant="ghost"
              size="sm"
              onClick={() =>
                navigate(paths.toPracticeProjectDetails(projectId!))
              }
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div className="min-w-0">
              <h1 className="text-xl font-bold text-gray-900 truncate">
                {practiceProject.title}
              </h1>
              <p className="text-sm text-gray-600">Jupyter Workspace</p>
            </div>
          </div>

          {/* Right: Status and actions */}
          <div className="flex items-center gap-3">
            {workspaceStatus.status === "active" && (
              <>
                <Badge
                  variant="outline"
                  className="bg-green-50 text-green-700 border-green-200"
                >
                  <CheckCircle2 className="w-3 h-3 mr-1" />
                  Active
                </Badge>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleStopWorkspace}
                >
                  <Square className="w-4 h-4 mr-2" />
                  Stop
                </Button>
              </>
            )}

            {workspaceStatus.status === "provisioning" && (
              <Badge variant="outline" className="bg-blue-50 text-blue-700">
                <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                Launching...
              </Badge>
            )}

            {workspaceStatus.status === "none" && (
              <Button onClick={handleLaunchWorkspace} disabled={!isEnrolled}>
                <Play className="w-4 h-4 mr-2" />
                Launch Sandbox
              </Button>
            )}

            {workspaceStatus.status === "error" && (
              <Button onClick={handleLaunchWorkspace} variant="outline">
                <RotateCcw className="w-4 h-4 mr-2" />
                Retry
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-hidden">
        {/* Not enrolled message */}
        {workspaceStatus.status === "none" && !isEnrolled && (
          <div className="h-full flex items-center justify-center p-8">
            <Card className="max-w-md">
              <CardHeader>
                <CardTitle>Enrollment Required</CardTitle>
                <CardDescription>
                  You need to enroll in this practice project before launching
                  the workspace.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button
                  onClick={async () => {
                    await enrollMutation.mutateAsync(projectId!);
                    toast.success("Enrolled successfully!");
                  }}
                  className="w-full"
                >
                  Enroll Now
                </Button>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Ready to launch */}
        {workspaceStatus.status === "none" && isEnrolled && (
          <div className="h-full flex items-center justify-center p-8">
            <Card className="max-w-2xl w-full">
              <CardHeader>
                <CardTitle>Ready to Launch</CardTitle>
                <CardDescription>
                  Your Jupyter workspace is ready to start. Click the button
                  below to launch.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Project info */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-1">
                      Difficulty
                    </h4>
                    <Badge variant="outline">
                      {practiceProject.difficulty_level}
                    </Badge>
                  </div>
                  {practiceProject.resource_profile_id && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-1">
                        Compute Profile
                      </h4>
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Cpu className="w-4 h-4" />
                        <span>Standard</span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Resources available */}
                {(practiceProject.dataset_s3_url?.length > 0 ||
                  practiceProject.additional_files_s3_urls?.length > 0 ||
                  practiceProject.notebook_template_s3_url) && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">
                      Resources Available
                    </h4>
                    <div className="space-y-1">
                      {practiceProject.notebook_template_s3_url && (
                        <div className="text-sm text-gray-600 flex items-center gap-2">
                          <HardDrive className="w-4 h-4" />
                          Notebook Template
                        </div>
                      )}
                      {practiceProject.dataset_s3_url && practiceProject.dataset_s3_url.length > 0 && (
                        <div className="text-sm text-gray-600 flex items-center gap-2">
                          <HardDrive className="w-4 h-4" />
                          {practiceProject.dataset_s3_url.length} Dataset(s)
                        </div>
                      )}
                      {practiceProject.additional_files_s3_urls && practiceProject.additional_files_s3_urls.length > 0 && (
                        <div className="text-sm text-gray-600 flex items-center gap-2">
                          <HardDrive className="w-4 h-4" />
                          {practiceProject.additional_files_s3_urls.length} Additional File(s)
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <Button
                  onClick={handleLaunchWorkspace}
                  className="w-full"
                  size="lg"
                >
                  <Play className="w-5 h-5 mr-2" />
                  Launch Jupyter Workspace
                </Button>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Provisioning */}
        {workspaceStatus.status === "provisioning" && (
          <div className="h-full flex items-center justify-center p-8">
            <Card className="max-w-md w-full">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Launching Workspace
                </CardTitle>
                <CardDescription>
                  Setting up your Jupyter environment...
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Progress value={workspaceStatus.provisioningProgress} />
                <div className="space-y-2 text-sm text-gray-600">
                  <div className="flex items-center gap-2">
                    {workspaceStatus.provisioningProgress >= 10 ? (
                      <CheckCircle2 className="w-4 h-4 text-green-600" />
                    ) : (
                      <Clock className="w-4 h-4" />
                    )}
                    <span>Initializing workspace</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {workspaceStatus.provisioningProgress >= 50 ? (
                      <CheckCircle2 className="w-4 h-4 text-green-600" />
                    ) : (
                      <Clock className="w-4 h-4" />
                    )}
                    <span>Starting Jupyter server</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {workspaceStatus.provisioningProgress >= 90 ? (
                      <CheckCircle2 className="w-4 h-4 text-green-600" />
                    ) : (
                      <Clock className="w-4 h-4" />
                    )}
                    <span>Loading notebooks</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Active workspace */}
        {workspaceStatus.status === "active" &&
          workspaceStatus.workspaceUrl && (
            <div className="h-full flex flex-col">
              <iframe
                src={workspaceStatus.workspaceUrl}
                className="w-full h-full border-0"
                title="Jupyter Workspace"
                allow="fullscreen"
                sandbox="allow-same-origin allow-scripts allow-forms allow-downloads allow-modals allow-popups"
              />
            </div>
          )}

        {/* Error state */}
        {workspaceStatus.status === "error" && (
          <div className="h-full flex items-center justify-center p-8">
            <Card className="max-w-md">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-red-600">
                  <AlertTriangle className="w-5 h-5" />
                  Launch Failed
                </CardTitle>
                <CardDescription>
                  {workspaceStatus.errorMessage ||
                    "Failed to launch workspace"}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <Alert>
                    <AlertDescription>
                      Please try again. If the problem persists, contact
                      support.
                    </AlertDescription>
                  </Alert>
                  <Button
                    onClick={handleLaunchWorkspace}
                    variant="outline"
                    className="w-full"
                  >
                    <RotateCcw className="w-4 h-4 mr-2" />
                    Try Again
                  </Button>
                  <Button
                    onClick={() =>
                      navigate(paths.toPracticeProjectDetails(projectId!))
                    }
                    variant="ghost"
                    className="w-full"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back to Project
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
